import {
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Cookies from "universal-cookie";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";

import { Title } from "../../components/Title.jsx";
import "../../stylesheets/Shipments.scss";

import {
  ShipmentsTableSearch,
  LoadingTable,
} from "../../components/table/shipments/ShipmentTable";
import { shipmentStatusOptions } from "../../components/table/shipments/Filter";

// modal
import CreateShipments from "./CreateShipments.jsx";

async function fetchWithTimeout(resource, options = {}) {
  const { timeout = 8000 } = options;
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  const response = await fetch(resource, {
    ...options,
    signal: controller.signal,
  });
  clearTimeout(id);
  return response;
}

const Shipments = () => {
  const location = useLocation();

  // — clear **only** when Box or breadcrumb sent us with clearFilters
  useEffect(() => {
    if (location.state?.clearFilters) {
      sessionStorage.removeItem("globalFilter");
      sessionStorage.removeItem("statusFilter");
      sessionStorage.removeItem("accountFilter");
      sessionStorage.removeItem("dateFilterOption");
      sessionStorage.removeItem("shipmentsPage");
      sessionStorage.removeItem("modeFilter");
      // erase the flag
      window.history.replaceState({}, "", location.pathname + location.search);
    }
  }, []);

  const [searchParams] = useSearchParams();
  const cookies = new Cookies();
  const token = cookies.get("token_user");
  const URL = process.env.REACT_APP_API_URL + "/user";
  const initial_url = `${URL}/shipments`;

  // ─── Pagination (hydrate) ───────────────────────────────────
  const [page, setPage] = useState(() => {
    const saved = sessionStorage.getItem("shipmentsPage");
    return saved ? parseInt(saved, 10) : 1;
  });
  const perPage = 100;
  useEffect(() => {
    sessionStorage.setItem("shipmentsPage", String(page));
  }, [page]);

  // ─── Filters (hydrate+persist) ──────────────────────────────
  const [filterState, setFilterState] = useState(() => {
    const global = sessionStorage.getItem("globalFilter") || "";

    // Status: now stored as JSON array
    let status = [];
    try {
      const raw = JSON.parse(sessionStorage.getItem("statusFilter") || "[]");
      if (Array.isArray(raw)) status = raw;
      else if (typeof raw === "string" && raw) status = [raw];
      else if (raw?.value) status = [raw.value];
    } catch (e) {}

    // Account: now stored as JSON array
    let account = [];
    try {
      const raw = JSON.parse(sessionStorage.getItem("accountFilter") || "[]");
      if (Array.isArray(raw)) account = raw;
      else if (typeof raw === "string" && raw) account = [raw];
      else if (raw?.value) account = [raw.value];
    } catch (e) {}

    // Mode: stored as JSON array
    let mode = [];
    try {
      const raw = JSON.parse(sessionStorage.getItem("modeFilter") || "[]");
      if (Array.isArray(raw)) mode = raw;
      else if (typeof raw === "string" && raw) mode = [raw];
    } catch (e) {}

    return { global, status, account, date: null, mode };
  });

  useEffect(() => {
    sessionStorage.setItem("globalFilter", filterState.global);
  }, [filterState.global]);
  useEffect(() => {
    sessionStorage.setItem(
      "statusFilter",
      JSON.stringify(filterState.status)
    );
  }, [filterState.status]);
  useEffect(() => {
    sessionStorage.setItem(
      "accountFilter",
      JSON.stringify(filterState.account)
    );
  }, [filterState.account]);

  useEffect(() => {
    sessionStorage.setItem("modeFilter", JSON.stringify(filterState.mode));
  }, [filterState.mode]);

  // ─── Data fetch state ───────────────────────────────────────
  const [shipments, setShipments] = useState(null);
  const [curShipments, setCurShipments] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const abortControllerRef = useRef(null);

  // Track whether the current fetch is a "first load" (needs summary counts)
  // vs. a plain page flip (skip the 8 heavy COUNT queries).
  // We want summary=1 whenever the filters change OR on the very first load.
  const isFirstLoadRef = useRef(true);
  const prevFilterKeyRef = useRef(null);

  // ─── Build URL on filter/page change ────────────────────────
  const shipmentsUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("per_page", perPage);

    const f = searchParams.get("filter");
    if (f) params.set("filter", f);

    // Join arrays with commas for the API
    const statusStr = Array.isArray(filterState.status)
      ? filterState.status.join(",")
      : filterState.status || "";
    const accountStr = Array.isArray(filterState.account)
      ? filterState.account.join(",")
      : filterState.account || "";

    const modeStr = Array.isArray(filterState.mode)
      ? filterState.mode.join(",")
      : filterState.mode || "";

    if (statusStr) params.set("status", statusStr);
    if (accountStr) params.set("account_no", accountStr);
    if (filterState.global) params.set("search", filterState.global);
    if (modeStr) params.set("mode_of_transport", modeStr);
    if (filterState.date?.length === 2) {
      params.set("date_from", filterState.date[0]);
      params.set("date_to", filterState.date[1]);
    }

    return `${initial_url}?${params.toString()}`;
  }, [
    initial_url,
    page,
    perPage,
    searchParams,
    filterState.status,
    filterState.account,
    filterState.global,
    filterState.mode,     // NEW
    filterState.date,
  ]);

  // ─── Fetch on URL change ────────────────────────────────────
  useEffect(() => {
    // Determine if this is a "summary" request (first load or filter change)
    const filterKey = [
      searchParams.toString(),
      filterState.status?.join(","),
      filterState.account?.join(","),
      filterState.global,
      filterState.mode?.join(","),
      filterState.date?.join(","),
    ].join("|");

    const needsSummary = isFirstLoadRef.current || filterKey !== prevFilterKeyRef.current;
    isFirstLoadRef.current = false;
    prevFilterKeyRef.current = filterKey;

    // Cancel any in-flight request before starting a new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    async function load() {
      setIsFetching(true);
      try {
        const urlWithSummary = needsSummary
          ? `${shipmentsUrl}&summary=1`
          : shipmentsUrl;

        const res = await fetchWithTimeout(urlWithSummary, {
          timeout: 30000,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        if (res.ok) {
          const data = await res.json();

          // Debug/visibility (you can remove later)
          console.log("[Shipments] URL:", urlWithSummary);
          console.log("[Shipments] top-level keys:", Object.keys(data || {}));
          console.log("[Shipments] pagination:", data?.pagination);
          console.log("[Shipments] first row:", Array.isArray(data?.data) ? data.data[0] : data?.data);
          console.log("[Shipments] any accounts field?", data?.accounts ?? data?.filters?.accounts ?? null);

          setShipments((prev) => {
            // On page flips (no summary), preserve the previous summary counts
            // so the dashboard boxes don't blank out.
            if (!needsSummary && prev) {
              return {
                ...data,
                under_exam: prev.under_exam,
                fda_exam: prev.fda_exam,
                pending_docs: prev.pending_docs,
                before_summary: prev.before_summary,
                "7501_to_review": prev["7501_to_review"],
                shipments_without_do: prev.shipments_without_do,
                pending_review: prev.pending_review,
                missing_info: prev.missing_info,
              };
            }
            return data;
          });
          setCurShipments(data);
        }
      } catch (err) {
        if (err.name === "AbortError") return; // request was intentionally cancelled
        console.error(err);
        toast.error("Failed to load shipments");
      } finally {
        if (!controller.signal.aborted) {
          setIsFetching(false);
        }
      }
    }
    load();
    return () => {
      controller.abort();
    };
  }, [shipmentsUrl, token]);


  // ─── Reset to page 1 on filter change (skip initial mount) ───
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    setPage(1);
  }, [
    filterState.global,
    filterState.status,
    filterState.account,
    filterState.date,
    filterState.mode,   // NEW
  ]);

  // ─── Pagination handlers & counters ────────────────────────
  const total = shipments?.pagination?.total || 0;
  const last = shipments?.pagination?.last_page || 1;
  const shown = Math.min(page * perPage, total);

  // Debounce so rapid consecutive clicks only fire one state update
  const gotoPreviousPage = useCallback(
    debounce(() => setPage((p) => Math.max(p - 1, 1)), 300, { leading: true, trailing: false }),
    []
  );
  const gotoNextPage = useCallback(
    debounce(
      () => {
        setPage((p) => {
          if (!shipments?.pagination) return p;
          return Math.min(p + 1, shipments.pagination.last_page);
        });
      },
      300,
      { leading: true, trailing: false }
    ),
    [shipments?.pagination?.last_page]
  );

  // ─── Create Shipment modal state ────────────────────────────
  const [openCreate, setOpenCreate] = useState(false);

  // Accounts list derived from payload (can be string "A, B" or array)
  const accounts = useMemo(() => {
    const raw = shipments?.accounts ?? shipments?.filters?.accounts ?? [];
    const arr = Array.isArray(raw) ? raw : (typeof raw === "string" ? raw.split(",") : []);
    const cleaned = arr.map(s => String(s).trim()).filter(Boolean);
    // Unique
    return Array.from(new Set(cleaned));
  }, [shipments]);

  // optional helper to refresh list after creation
  // Force a re-fetch by toggling to page 1 (AbortController will cancel any in-flight request)
  const refreshAfterCreate = () => {
    isFirstLoadRef.current = true; // treat next fetch as a first load so summary counts refresh
    setPage(1);
  };


  const csvEscape = (value) => {
    if (value === null || value === undefined) return "";
    let s = "";
    if (Array.isArray(value)) {
      s = value
        .filter((item) => item !== null && item !== undefined && String(item).trim() !== "")
        .map((item) =>
          typeof item === "object" && item !== null
            ? item.master_bill_lading || item.house_bill_lading || item.container || item.number || JSON.stringify(item)
            : String(item).trim()
        )
        .join("; ");
    } else {
      s = String(value);
    }
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const formatDate = (value) => {
    if (!value || value === "0000-00-00 00:00:00") return "";
    return String(value).split("T")[0].split(" ")[0];
  };

  const buildShipmentsCsv = (rows) => {
    const headers = [
      "Entry Number",
      "Master Bill of Lading",
      "House Bill of Lading",
      "Container Number(s)",
      "Reference Number",
      "ETA",
      "Client",
      "FDA",
      "Status",
    ];

    const lines = [headers.map(csvEscape).join(",")];

    for (const s of rows || []) {
      lines.push(
        [
          csvEscape(s.file_number),
          csvEscape(s.master_bill_lading),
          csvEscape(s.house_bill_lading),
          csvEscape(s.containers),
          csvEscape(s.customer_reference_no),
          csvEscape(formatDate(s.est_time_arrive)),
          csvEscape(s.account_no),
          csvEscape(s.fda_review),
          csvEscape(s.status),
        ].join(",")
      );
    }

    return lines.join("\n");
  };

  const [exportingCsv, setExportingCsv] = useState(false);

  const downloadAllShipmentsCsv = async () => {
    setExportingCsv(true);
    try {
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("per_page", "10000");

      const f = searchParams.get("filter");
      if (f) params.set("filter", f);

      const statusStr = Array.isArray(filterState.status)
        ? filterState.status.join(",")
        : filterState.status || "";
      const accountStr = Array.isArray(filterState.account)
        ? filterState.account.join(",")
        : filterState.account || "";
      const modeStr = Array.isArray(filterState.mode)
        ? filterState.mode.join(",")
        : filterState.mode || "";

      if (statusStr) params.set("status", statusStr);
      if (accountStr) params.set("account_no", accountStr);
      if (filterState.global) params.set("search", filterState.global);
      if (modeStr) params.set("mode_of_transport", modeStr);
      if (filterState.date?.length === 2) {
        params.set("date_from", filterState.date[0]);
        params.set("date_to", filterState.date[1]);
      }

      const exportUrl = `${initial_url}?${params.toString()}`;

      const res = await fetchWithTimeout(exportUrl, {
        timeout: 15000,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (res.ok) {
        const data = await res.json();
        const allRows = data?.data ?? data?.result?.data ?? data?.result ?? [];
        const csv = buildShipmentsCsv(allRows);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });

        const today = new Date().toISOString().slice(0, 10);
        const filename = `shipments_${today}_all.csv`;

        const link = document.createElement("a");
        const href = window.URL.createObjectURL(blob);
        link.href = href;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(href);

        toast.success(`Exported ${allRows.length} shipments`);
      } else {
        toast.error("Failed to fetch shipments for export");
      }
    } catch (e) {
      console.error("Export error:", e);
      toast.error("Error exporting shipments");
    } finally {
      setExportingCsv(false);
    }
  };

  return (
    <>
      <Title
        title="Shipments"
        links={[
          {
            link: "/",
            title: "Home",
            // clear when you click the breadcrumb
            state: { clearFilters: true },
          },
          { link: "/shipments", title: "Shipments" },
        ]}
      />

      <div id="shipments" className="page-content">
        <div className="data-table">
          {curShipments ? (
            <>
              <ShipmentsTableSearch
                shipments={curShipments}
                filterState={filterState}
                setFilterState={setFilterState}
                statusOptions={shipmentStatusOptions}
                onDownloadCsv={
                  Number(cookies.get("role_user") || cookies.get("role")) === 11
                    ? null
                    : downloadAllShipmentsCsv
                }
                exportingCsv={exportingCsv}
                onCreateShipment={() => setOpenCreate(true)}
              />

              <div className="pagination">
                {page > 1 && (
                  <button
                    onClick={gotoPreviousPage}
                    disabled={isFetching}
                    style={{ opacity: isFetching ? 0.5 : 1, cursor: isFetching ? "not-allowed" : "pointer" }}
                  >
                    {"< Previous"}
                  </button>
                )}
                <span className="text">
                  {isFetching ? "Loading…" : `${shown}/${total}`}
                </span>
                {page < last && (
                  <button
                    onClick={gotoNextPage}
                    disabled={isFetching}
                    style={{ opacity: isFetching ? 0.5 : 1, cursor: isFetching ? "not-allowed" : "pointer" }}
                  >
                    {"Next >"}
                  </button>
                )}
              </div>

            </>
          ) : (
            <LoadingTable />
          )}
        </div>
      </div>

      {/* Modal */}
      <CreateShipments
        isOpen={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={() => {
          refreshAfterCreate();
          if (typeof window.getShipmentData === "function") {
            window.getShipmentData();
          }
        }}
        accounts={accounts}
      />
    </>
  );
};

export default Shipments;
